/**
 * Le contrat de la liquidité du réseau, côté navigateur (`EX-LIQ-003`,
 * `EX-BO-004`, écran `B-21`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `LiquidityMessages`, sans rien y ajouter. Un
 * contrôle confronte champ par champ cette déclaration au fichier Java
 * lui-même : un champ renommé d'un seul côté produit un écran vide **sans
 * erreur**.
 *
 * ## Ce que ce chantier referme, et ce qu'il a fallu écrire
 *
 * `LiquidityApi` servait **deux routes, éprouvées, et appelées par aucun
 * écran** — le tableau de bord de la liquidité et la file des demandes de
 * rééquilibrage. C'est la famille de `D-25` / `D-30` / `D-35`.
 *
 * Mais il manquait une **troisième**, et son absence rendait l'action
 * inutilisable : `LIQUIDITY_REQUEST_ASSIGN` exige l'identifiant d'un agent, et
 * rien ne disait lesquels existent. L'exploitant ne pouvait affecter une
 * tournée qu'en connaissant déjà un `UUID`. C'est le défaut que le module
 * Clients a payé, mot pour mot — « la couche qui permet de **trouver**
 * manquait sous la couche qui permet d'**agir** » —, et il se répétait ici sans
 * que personne l'ait vu, parce qu'aucun écran n'appelait encore la moitié qui
 * existait. `GET /liquidity/agents` a donc été écrite, sous le droit qui
 * existait déjà.
 *
 * ## `rateIsMeasured` NE SE TAIT JAMAIS
 *
 * C'est le champ le plus important de ce contrat. Un taux de service de 100 %
 * sur zéro opération est **vrai** et ne démontre rien — c'est la somme nulle du
 * contrôle d'équilibre du grand livre, et c'est ce qui a laissé `D-53` vivre :
 * trois contrôles financiers écrits, verts, que rien n'appelait, sur une pile
 * qui portait 34 écritures pour 0 photo. Le serveur dit explicitement si la
 * mesure porte sur quelque chose ; l'écran le rend, et ne peint jamais un
 * pourcentage seul.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin du tableau de bord, tel que `LiquidityApi` le sert. */
export const liquidityDashboardPath = '/api/backoffice/liquidity/dashboard';

/** Le chemin de la file des demandes de rééquilibrage. */
export const liquidityRequestsPath = '/api/backoffice/liquidity/requests';

/** Le chemin des agents de terrain — la lecture qui manquait sous l'action. */
export const liquidityAgentsPath = '/api/backoffice/liquidity/agents';

/** Le chemin de la route unique des actions habilitables. */
export const liquidityActionsPath = '/api/backoffice/actions';

/** Le motif écrit au journal pour toute lecture de ce module. */
export const liquidityReason = 'pilotage-de-la-liquidite';

/** Le code d'action de l'affectation d'une tournée. */
export const assignRoundAction = 'LIQUIDITY_REQUEST_ASSIGN';

/**
 * Le canal d'une demande que l'on peut affecter.
 *
 * Le serveur le défend : une demande servie par un guichet voisin **n'a
 * personne à qui être affectée**, et l'exécuteur la refuse en le disant. La
 * recopier ici permet à l'écran de ne pas offrir un bouton qui sera refusé — la
 * garde reste côté serveur, l'écran cesse seulement de promettre.
 */
export const assignableChannel = 'AGENT';

/**
 * La position de liquidité d'un guichet. Suit
 * `LiquidityMessages.OutletLiquidityView`.
 *
 * `electronicFloat` et `daysOfAutonomy` peuvent manquer, et ce ne sont **pas**
 * des zéros : un guichet dont l'encours n'a pas été relevé n'est pas un guichet
 * à sec, et une autonomie inconnue n'est pas une autonomie nulle. Les rendre à
 * zéro ferait partir une collecte vers un guichet qui n'en a pas besoin — ou,
 * pire, la ferait manquer.
 *
 * `latitude` et `longitude` sont servies et **ne sont pas employées** : voir
 * l'écran. Une carte géographique répondrait moins bien que l'ordre à la seule
 * question qui se pose, « lequel dois-je réapprovisionner d'abord ».
 */
export interface OutletLiquidity {
  readonly outletId: string;
  readonly outletCode: string;
  readonly businessName: string;
  /**
   * La région administrative — **elle manque souvent**, et ce n'est pas une
   * panne : dix-sept guichets sur vingt-trois n'en portent aucune dans la base
   * de développement. Une cellule vide se lirait comme un défaut d'affichage ;
   * l'écran dit « non relevé », comme pour l'encours et l'autonomie.
   */
  readonly region: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly electronicFloat: number | null;
  readonly theoreticalCash: number;
  readonly floatLowThreshold: number;
  readonly cashHighThreshold: number;
  readonly underTension: boolean;
  readonly forecastDailyNeed: number;
  readonly daysOfAutonomy: number | null;
  readonly servedOperations: number;
  readonly liquidityFailures: number;
  readonly liquidityFailurePercent: number;
}

/**
 * Une demande de rééquilibrage. Suit `LiquidityMessages.RequestView`.
 *
 * `assignedAgentName` est un **nom**, et il n'arrive qu'une fois la tournée
 * affectée. `counterpartyOutletCode` ne vaut que pour une demande servie par un
 * guichet voisin — et celle-là n'a personne à qui être affectée.
 */
export interface LiquidityRequest {
  readonly id: string;
  readonly outletCode: string | null;
  readonly outletName: string | null;
  readonly outletAddress: string | null;
  readonly requestType: string;
  readonly channel: string;
  readonly amount: number;
  readonly status: string;
  readonly assignedAgentName: string | null;
  readonly counterpartyOutletCode: string | null;
  readonly createdAt: string;
}

/**
 * Un agent de terrain. Suit `LiquidityMessages.FieldAgentView`.
 *
 * `cashLimit` peut manquer, et ce n'est **pas** zéro : un plafond absent veut
 * dire « personne ne l'a borné », pas « cet agent ne peut rien porter ».
 * L'écran affiche l'absence, et l'exploitant sait qu'il affecte une tournée
 * sans borne.
 */
export interface FieldAgent {
  readonly id: string;
  readonly fullName: string;
  readonly cashLimit: number | null;
  readonly zone: string | null;
}

/**
 * Le tableau de bord de la liquidité. Suit
 * `LiquidityMessages.NetworkLiquidityView`.
 *
 * **`rateIsMeasured` ne se tait jamais.** Voir l'en-tête : un taux de service
 * de 100 % sur zéro opération est vrai et ne démontre rien.
 *
 * `windowDays` est la fenêtre que le **serveur** a appliquée, lue en
 * configuration. L'écran la rend plutôt que de la supposer : un taux affiché
 * sans sa fenêtre est un taux dont personne ne sait sur quoi il porte.
 */
export interface NetworkLiquidity {
  readonly outlets: number;
  readonly outletsUnderTension: number;
  readonly servedOperations: number;
  readonly liquidityFailures: number;
  readonly liquidityFailurePercent: number;
  readonly serviceRatePercent: number;
  readonly rateIsMeasured: boolean;
  readonly windowDays: number;
  readonly positions: readonly OutletLiquidity[];
  readonly openRequests: readonly LiquidityRequest[];
}

/** Ce que rend la route des actions. */
export interface ActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/**
 * Lit le tableau de bord de la liquidité.
 *
 * Aucune capture d'erreur : rendre un tableau vide ferait confondre « aucun
 * guichet n'est en tension » — un fait — avec « le serveur a refusé », qui n'en
 * est pas un. Sur l'écran qui décide d'envoyer une collecte, la confusion se
 * lit comme un réseau sain.
 */
export async function fetchNetworkLiquidity(
  client: ApiClient,
  reason: string = liquidityReason,
): Promise<NetworkLiquidity> {
  const parametres = new URLSearchParams({ reason });
  const vue = await client.request<NetworkLiquidity>(
    `${liquidityDashboardPath}?${parametres.toString()}`,
  );
  return exigerUnTableau(vue);
}

/** Lit la file des demandes de rééquilibrage ouvertes. */
export async function fetchLiquidityRequests(
  client: ApiClient,
  reason: string = liquidityReason,
): Promise<readonly LiquidityRequest[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly LiquidityRequest[]>(
    `${liquidityRequestsPath}?${parametres.toString()}`,
  );
}

/** Lit les agents de terrain à qui une tournée peut être affectée. */
export async function fetchFieldAgents(
  client: ApiClient,
  reason: string = liquidityReason,
): Promise<readonly FieldAgent[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly FieldAgent[]>(
    `${liquidityAgentsPath}?${parametres.toString()}`,
  );
}

/**
 * Affecte une tournée à un agent.
 *
 * Le motif est **saisi**, demande par demande : un exploitant affecte pour une
 * raison qui lui est propre — un guichet à sec avant un jour de marché n'est
 * pas une tournée de routine —, et c'est elle que le journal doit porter.
 */
export async function assignRound(
  client: ApiClient,
  demande: {
    readonly requestId: string;
    readonly agentId: string;
    readonly reason: string;
  },
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(
    `${liquidityActionsPath}/${assignRoundAction}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityRef: demande.requestId,
        reason: demande.reason,
        payload: { requestId: demande.requestId, agentId: demande.agentId },
      }),
    },
  );
}

/**
 * Refuse ce qui n'est **pas** un tableau de bord de liquidité.
 *
 * L'écran s'écroulerait sur une lecture de `positions.length`, et l'exploitant
 * verrait une page blanche là où il vient chercher quel guichet
 * réapprovisionner. Mieux vaut « le serveur a répondu quelque chose
 * d'inutilisable » qu'un réseau qui paraît sain.
 */
function exigerUnTableau(vue: NetworkLiquidity): NetworkLiquidity {
  if (!Array.isArray(vue?.positions) || typeof vue?.rateIsMeasured !== 'boolean') {
    throw new Error(
      'ÉCHEC : la réponse n’est pas un tableau de bord de liquidité — les ' +
        'positions et le drapeau de mesure sont ce qui le définit.',
    );
  }
  return vue;
}
